import { navigate } from "gatsby";
import React, { useCallback } from "react";
import {
  IoIosArrowDropleftCircle,
  IoIosArrowDroprightCircle,
} from "react-icons/io";

interface BottomSyncNavigationProps {
  prev?: {
    to: string;
    label: string;
  };
  next?: {
    to: string;
    label: string;
  };
  className?: string;
  loading: boolean;
  onNavigate: ((path: string) => Promise<void>) | (() => void);
}

/**
 * Enhanced navigation component that supports data syncing before navigation
 */
const BottomSyncNavigation: React.FC<BottomSyncNavigationProps> = ({
  prev,
  next,
  loading,
  className = "",
  onNavigate,
}) => {
  const handleNavigation = useCallback(
    async (path: string) => {
      const isNavigatingForward = path === next?.to;
      const isNavigatingBackward = path === prev?.to;

      const navigateToPath = () => {
        navigate(path);
      };

      const saveThenNavigate = async () => {
        if (!onNavigate) {
          navigateToPath();
          return;
        }

        try {
          // Call onNavigate with path - it handles the navigation internally
          await (onNavigate as (path: string) => Promise<void>)(path);
          // Don't navigate here - onNavigate handles it
        } catch (saveError) {
          console.error(saveError);
          alert("Failed to save your answers. Please try again.");
        }
      };

      if (isNavigatingForward || isNavigatingBackward) {
        await saveThenNavigate();
        return;
      }

      navigateToPath();
    },
    [onNavigate, prev, next]
  );

  return (
    <div
      style={{
        marginTop: "40px",
        marginBottom: "20px",
        fontFamily: "Work Sans",
        fontSize: "17px",
      }}
      className={`flex w-full justify-between text-sm font-semibold items-center  leading-[1] text-site-blue-dark ${className}`}
    >
      <div>
        {prev && (
          <div
            className="cursor-pointer"
            onClick={() => handleNavigation(prev.to)}
          >
            <div className="flex items-center gap-1">
              <div className="w-5xl flex justify-center items-center">
                <IoIosArrowDropleftCircle size={64} color="#00b8a4" />
              </div>
              <div className="flex-1 min-w-0 pr-2">
                <span className="whitespace-normal">{prev.label}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div>
        {next && (
          <div
            className="cursor-pointer"
            onClick={() => handleNavigation(next.to)}
          >
            <div className="flex items-center gap-1 text-right">
              <div className="flex-1 min-w-0 pl-2">
                <span className="whitespace-normal">
                  {loading ? "Saving..." : next.label}
                </span>
              </div>
              <div className="w-5xl flex justify-center items-center">
                <IoIosArrowDroprightCircle size={64} color="#00b8a4" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BottomSyncNavigation;
